import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styles from './WorkersList.module.scss';
import { useDispatch, useSelector } from 'react-redux';
import { getService, getWorkers } from '../../../../store/selectors';
import SelectorMenu from '../../../../../Common/CustomSelector/SelectorMenu';
import { Button } from '@mui/material';
import { salonManagementActions } from '../../../../store';
import { useNavigate, useParams } from 'react-router-dom';
import { getActiveSalonId } from '../../../../../Auth/store/selectors';
import TitleSectionBack from '../../../../../Common/TitleSectionBack';
import AvatarIcon from '../../../../../Common/AvatarIcon';
import useWindowSize from '../../../../../../utils/useWindowSize';
import clx from 'classnames';
import { useTranslation } from 'react-i18next';

const sameIds = (a = [], b = []) => {
  if (a.length !== b.length) return false;
  const sortedA = [...a].sort((x, y) => x - y);
  const sortedB = [...b].sort((x, y) => x - y);
  return sortedA.every((id, index) => id === sortedB[index]);
};

const WorkersList = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { serviceId } = useParams();
  const { isMobile } = useWindowSize();
  const navigate = useNavigate();
  const activeSalonId = useSelector(getActiveSalonId());
  const workers = useSelector(getWorkers()) || [];
  const service = useSelector(getService());
  const [activeWorkers, setActiveWorkers] = useState(service.activeWorkersIds || []);

  const formedWorkers = useMemo(
    () =>
      workers.map((worker) => ({
        name: worker.name,
        value: worker.id,
        icon: worker.icon,
        disabled: worker.disabilitato,
        size: '42px',
      })),
    [workers],
  );

  const enabledWorkersCount = useMemo(
    () => formedWorkers.filter((worker) => !worker.disabled).length,
    [formedWorkers],
  );

  const hasChanges = useMemo(
    () => !sameIds(activeWorkers, service.activeWorkersIds || []),
    [activeWorkers, service.activeWorkersIds],
  );

  useEffect(() => {
    if (service.activeWorkersIds) {
      setActiveWorkers(service.activeWorkersIds);
    }
  }, [service.activeWorkersIds]);

  useEffect(() => {
    dispatch(salonManagementActions.GET_WORKERS.REQUEST({}, null, { skipLoader: true }));
    dispatch(salonManagementActions.GET_SERVICE.REQUEST(serviceId, null, { skipLoader: true }));
    return () => {
      dispatch(salonManagementActions.GET_WORKERS.CLEAR());
      dispatch(salonManagementActions.GET_SERVICE.CLEAR());
    };
  }, [dispatch, serviceId]);

  useEffect(() => {
    if (service?.salone && service?.salone !== activeSalonId) {
      dispatch(salonManagementActions.GET_WORKERS.CLEAR());
      dispatch(salonManagementActions.GET_SERVICE.CLEAR());
      navigate(-1);
    }
  }, [service, activeSalonId, dispatch, navigate]);

  const saveValue = useCallback(() => {
    if (!hasChanges) return;

    const currentIds = service.activeWorkersIds || [];
    const activeWorkersIds = activeWorkers.filter((id) => !currentIds.includes(id));
    const workersIdsToDelete = currentIds.filter((id) => !activeWorkers.includes(id));

    if (!activeWorkersIds.length && !workersIdsToDelete.length) return;

    dispatch(
      salonManagementActions.EDIT_SERVICE_WORKERS.REQUEST(
        {
          idServizio: service.idservezio,
          activeWorkersIds,
          workersIdsToDelete,
        },
        () => navigate(-1),
      ),
    );
  }, [
    activeWorkers,
    dispatch,
    hasChanges,
    navigate,
    service.activeWorkersIds,
    service.idservezio,
  ]);

  const saveButton = (
    <Button
      onClick={saveValue}
      className={styles.confirm_button}
      variant="contained"
      type="button"
      disabled={!hasChanges}
    >
      {t('services.workersList.save')}
    </Button>
  );

  const serviceChip = (
    <div className={styles.service_chip}>
      <AvatarIcon name={service.nome} icon={service.icon} size="40px" />
      <div className={styles.service_chip_copy}>
        <span>{t('services.workersList.associatingService')}</span>
        <strong>{service.nome || '—'}</strong>
      </div>
    </div>
  );

  return (
    <div
      className={clx(styles.page, {
        [styles.page_mobile]: isMobile,
      })}
    >
      <div className={styles.top_bar}>
        <TitleSectionBack
          title={t('services.workersList.title')}
          description={t('services.workersList.description')}
        />
        {!isMobile ? (
          <div className={styles.top_actions}>
            {serviceChip}
            {saveButton}
          </div>
        ) : null}
      </div>

      {isMobile ? <div className={styles.mobile_banner}>{serviceChip}</div> : null}

      <section className={styles.chairs_card}>
        <div className={styles.chairs_card_header}>
          <div className={styles.chairs_card_title_block}>
            <h3 className={styles.chairs_card_title}>{t('services.workersList.chairs')}</h3>
            <p className={styles.chairs_card_hint}>{t('services.workersList.chairsHint')}</p>
          </div>
          <span className={styles.chairs_count_badge}>
            {t('services.workersList.selectedCount', {
              selected: activeWorkers.length,
              total: enabledWorkersCount,
            })}
          </span>
        </div>

        <div className={styles.chairs_list}>
          <SelectorMenu
            isMultiple
            options={formedWorkers}
            setIsDropdownOpen={() => {}}
            setValue={setActiveWorkers}
            value={activeWorkers}
            fullHeight
            border={false}
            compact
            isEmptyMessage={t('services.workersList.emptyChairs')}
          />
        </div>
      </section>

      {isMobile ? <footer className={styles.footer}>{saveButton}</footer> : null}
    </div>
  );
};

export default WorkersList;
